import "./PdpPanel.scss";
import PdpEditor from "./PdpEditor";
import {usePdpPermissions} from "../../hooks/usePdpPermissions";
import { useEffect, useState } from "react";
import PdpService, { PdpListing, errorMessage } from "../../services/PdpService";

export default function PdpPanel({ refreshKey, onUpdated }: { refreshKey: unknown; onUpdated?: () => void }) {
  const {canManagePdp}=usePdpPermissions();
  const [editing,setEditing]=useState<number | null>(null);
  const [status,setStatus]=useState("EXPIRING_SOON");
  const [skip,setSkip]=useState(0);
  const [data,setData]=useState<PdpListing | null>(null);
  const [reminders,setReminders]=useState(0);
  const [error,setError]=useState("");
  const [loading,setLoading]=useState(false);
  const [revision,setRevision]=useState(0);
  useEffect(()=>{
    const timer=window.setInterval(()=>setRevision(r=>r+1),60000);
    return ()=>window.clearInterval(timer);
  },[]);
  useEffect(()=>{
    const controller=new AbortController();setLoading(true);setError("");setData(null);
    Promise.all([PdpService.list(status,skip,controller.signal),PdpService.alerts(controller.signal)])
      .then(([listing,alerts])=>{if(!controller.signal.aborted){setData(listing);setReminders(alerts.totalElements);}})
      .catch(e=>{if(!controller.signal.aborted)setError(errorMessage(e));})
      .finally(()=>{if(!controller.signal.aborted)setLoading(false);});
    return ()=>controller.abort();
  },[status,skip,refreshKey,revision]);
  return <section aria-label="PDP expiry tracking" className="pdp-panel">
    <div className="pdp-panel__header">
      <div><h2>PDP expiry tracking</h2><p className="pdp-panel__description">Monitor driver permits and upcoming renewals.</p></div>
      {data && <span className="pdp-panel__count">{reminders} current {reminders === 1 ? "reminder" : "reminders"}</span>}
    </div>
    {editing !== null && <PdpEditor key={editing} driverId={editing} onClose={()=>setEditing(null)} onSaved={()=>{setRevision(r=>r+1);onUpdated?.();}}/>}
    <p className="pdp-panel__policy">Renewal is unavailable on Sundays or from 6 PM in the configured port timezone.</p>
    <div className="pdp-panel__toolbar"><div className="pdp-panel__filter">
    <label htmlFor="pdp-status">PDP status </label>
    <select id="pdp-status" value={status} onChange={e=>{setStatus(e.target.value);setSkip(0);}}>
      <option value="EXPIRING_SOON">Expiring soon</option><option value="EXPIRED">Expired</option>
      <option value="MISSING">Missing PDP</option><option value="VALID">Valid</option><option value="">All</option>
    </select></div>
    <button className="pdp-button pdp-button--secondary" type="button" onClick={()=>setRevision(r=>r+1)} disabled={loading}>{loading ? "Refreshing..." : "Refresh PDPs"}</button></div>
    {loading && <p className="pdp-panel__state" role="status">Loading PDP records...</p>}
    {error && <div className="pdp-panel__error" role="alert"><strong>Unable to load PDP records</strong><span>{error}</span></div>}
    {data && <><div className="pdp-panel__table-wrap"><table className="pdp-panel__table">
      <thead><tr><th>Driver</th><th>PDP number</th><th>Expiry date</th><th>Status</th><th>Renew before</th><th>Action</th></tr></thead>
      <tbody>{data.content.map(d=><tr key={d.driverId}>
        <td>{d.driverName}</td><td>{d.pdpNumber || "Not recorded"}</td><td>{d.pdpExpiryDate || "Not recorded"}</td>
        <td><span className={`pdp-status pdp-status--${d.status.toLowerCase()}`}>{d.status.replace(/_/g," ")}</span></td><td>{d.renewalDeadline ? new Date(d.renewalDeadline).toLocaleString() : "-"}</td>
        <td>{canManagePdp && <button className="pdp-button pdp-button--link" type="button" onClick={()=>setEditing(d.driverId)}>Edit PDP</button>}</td>
      </tr>)}</tbody>
    </table></div>{data.content.length===0 && <p className="pdp-panel__state">No matching PDP records.</p>}
    <div className="pdp-panel__pagination"><button className="pdp-button pdp-button--secondary" type="button" disabled={skip===0 || loading} onClick={()=>setSkip(s=>Math.max(0,s-30))}>Previous</button>
    <span> {data.totalElements} records </span>
    <button className="pdp-button pdp-button--secondary" type="button" disabled={skip+30>=data.totalElements || loading} onClick={()=>setSkip(s=>s+30)}>Next</button></div></>}
  </section>;
}
